import {
    Box,
    Checkbox,
    Divider,
    IconButton,
    InputAdornment,
    ListItemText,
    Menu,
    MenuItem,
    Stack,
    SvgIcon,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import SearchIcon from "@mui/icons-material/Search";
import ViewColumnOutlinedIcon from "@mui/icons-material/ViewColumnOutlined";
import _ from "lodash";
import React, { useEffect, useMemo, useState } from "react";
import { subscribeChartTableSearchNav } from "./chartTableSearchNavBridge";

export const tableDefaultQuestions = [
    { question: { tableLabel: "Last Name", label: "Last Name", accessor: "lastName", _id: "lastName" } },
    { question: { tableLabel: "First Name", label: "First Name", accessor: "firstName", _id: "firstName" } },
    { question: { tableLabel: "Date Of Birth", label: "Date Of Birth", accessor: "DOB", _id: "DOB" } },
    { question: { tableLabel: "Type", label: "Type", accessor: "type", _id: "type" } },
    { question: { tableLabel: "Hospital Days", label: "Hospital Days", accessor: "hospitalDays", _id: "hospitalDays" } },
];

const getColumnDisplayLabel = (column) =>
    column?.question?.tableLabel || column?.question?.label || "";

const COLUMN_MENU_SEARCH_THRESHOLD = 6;

export default function ChartDetailsSearchBar({
    handleSearch,
    searchTerm,
    highlightedCount,
    filterType,
    setFilterType,
    setSelectedHighlightedColumns: setSelectedColumns,
    selectedHighlightedColumns: selectedColumns,
    selectedQuestions: selectedQuestionsData,
    isSelectColumn = true,
    isMobile = false,
}) {
    const [selectedQuestions, setSelectedQuestions] = useState([]);
    const [columnFilterQuery, setColumnFilterQuery] = useState("");
    const [columnMenuAnchorEl, setColumnMenuAnchorEl] = useState(null);
    const [matchNav, setMatchNav] = useState({
        total: 0,
        activeIndex: 0,
        columnLabel: "",
        columnMatchIndex: 0,
        columnMatchTotal: 0,
        goPrev: null,
        goNext: null,
    });

    useEffect(() => {
        if (selectedQuestionsData && selectedQuestionsData.length > 0) {
            setSelectedQuestions([...selectedQuestionsData]);
        }
    }, [selectedQuestionsData]);

    useEffect(() => subscribeChartTableSearchNav(setMatchNav), []);

    const isLessThan = filterType.operation === "lessThan";
    const isGreaterThan = filterType.operation === "greaterThan";
    const [defaultQuestions, setDefaultQuestions] = useState([]);

    const questionData = useMemo(() => {
        if (selectedQuestions && selectedQuestions.length > 0) {
            return [...tableDefaultQuestions, ...selectedQuestions];
        }
        return [];
    }, [selectedQuestions]);

    useEffect(() => {
        setDefaultQuestions(questionData);
    }, [questionData]);

    const controlHeight = isMobile ? 36 : 40;
    const labelOffset = isMobile ? "-8px" : "-4px";

    const columnAccessors = useMemo(
        () => defaultQuestions?.map((ele) => ele?.question?.accessor).filter(Boolean) || [],
        [defaultQuestions]
    );

    const selectedColumnCount = useMemo(
        () => columnAccessors.filter((accessor) => selectedColumns.includes(accessor)).length,
        [columnAccessors, selectedColumns]
    );

    const isAllColumnsSelected =
        selectedColumns.includes("selectAll") ||
        (columnAccessors.length > 0 && selectedColumnCount === columnAccessors.length);

    const isSomeColumnsSelected = selectedColumnCount > 0 && !isAllColumnsSelected;

    const filteredQuestions = useMemo(() => {
        const query = columnFilterQuery.trim().toLowerCase();
        if (!query) return defaultQuestions;
        return defaultQuestions.filter((column) =>
            getColumnDisplayLabel(column).toLowerCase().includes(query)
        );
    }, [defaultQuestions, columnFilterQuery]);

    const handleToggleColumn = (accessor) => {
        const withoutSelectAll = selectedColumns.filter((item) => item !== "selectAll");
        let next;

        if (withoutSelectAll.includes(accessor)) {
            next = withoutSelectAll.filter((item) => item !== accessor);
        } else {
            next = [...withoutSelectAll, accessor];
        }

        const allSelected =
            columnAccessors.length > 0 && next.length === columnAccessors.length;

        setSelectedColumns(allSelected ? [...next, "selectAll"] : next);
    };

    const handleChangeSelectAll = (event) => {
        const checked = event.target.checked;
        if (checked) {
            setSelectedColumns([...columnAccessors, "selectAll"]);
        } else {
            setSelectedColumns([]);
        }
    };

    const handleOpenColumnMenu = (event) => {
        setColumnMenuAnchorEl(event.currentTarget);
    };

    const handleColumnMenuClose = () => {
        setColumnMenuAnchorEl(null);
        setColumnFilterQuery("");
    };

    const renderSelectedColumnsSummary = () => {
        if (!selectedColumns?.length || !columnAccessors.length) {
            return "All columns";
        }
        if (selectedColumns.includes("selectAll")) {
            return "All columns";
        }
        const selectedItems =
            defaultQuestions?.filter((ele) =>
                _.includes(selectedColumns, ele?.question?.accessor)
            ) || [];
        if (!selectedItems.length) return "All columns";
        const labels = selectedItems.map(getColumnDisplayLabel);
        if (labels.length <= 3) return labels.join(", ");
        return `${labels.slice(0, 2).join(", ")} +${labels.length - 2} more`;
    };

    const showColumnMenuSearch = defaultQuestions.length >= COLUMN_MENU_SEARCH_THRESHOLD;
    const hasSearchTerm = searchTerm !== "" && searchTerm != null;
    const matchTotal = matchNav.total ?? highlightedCount ?? 0;
    const matchPosition =
        matchTotal > 0 ? Math.min((matchNav.activeIndex ?? 0) + 1, matchTotal) : 0;

    const showColumnPicker =
        selectedQuestions && selectedQuestions.length > 0 && isSelectColumn;

    return (
        <Stack
            direction="row"
            spacing={1}
            alignItems="flex-start"
            sx={{
                mt: isMobile ? 0 : 0.5,
                mb: isMobile ? -0.5 : 0,
                flexWrap: isMobile ? "wrap" : "nowrap",
                rowGap: 1
            }}
        >
            <TextField
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={handleSearch}
                sx={{
                    width: isMobile ? "100%" : 360,
                    maxWidth: isMobile ? "100%" : 420,
                    flex: isMobile ? "1 1 100%" : undefined,
                }}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon sx={{ fontSize: 18, color: "#6b7280" }} />
                        </InputAdornment>
                    ),
                    endAdornment: showColumnPicker ? (
                        <InputAdornment position="end">
                            <Tooltip title="Choose columns to search" arrow>
                                <IconButton
                                    size="small"
                                    edge="end"
                                    onClick={handleOpenColumnMenu}
                                    aria-label="Choose search columns"
                                    sx={{
                                        color: isAllColumnsSelected ? "#6b7280" : "#4879F5",
                                    }}
                                >
                                    <ViewColumnOutlinedIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                            </Tooltip>
                        </InputAdornment>
                    ) : null,
                    sx: {
                        height: controlHeight,
                        alignItems: "center",
                        borderRadius: 1.5,
                        fontFamily: "manrope-reg, sans-serif",
                        pr: showColumnPicker ? 0.5 : undefined,
                    },
                }}
                InputLabelProps={{
                    sx: { top: labelOffset, fontFamily: "manrope-reg, sans-serif" },
                }}
                helperText={
                    showColumnPicker
                        ? `Searching in: ${renderSelectedColumnsSummary()}`
                        : undefined
                }
                FormHelperTextProps={{
                    sx: {
                        mt: 0.25,
                        fontSize: 11,
                        color: "#6b7280",
                        fontFamily: "manrope-reg, sans-serif",
                    },
                }}
            />

            {hasSearchTerm && (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.25,
                        flexShrink: 0,
                        mt: isMobile ? 0 : 0.25,
                        height: controlHeight,
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            px: isMobile ? 1 : 1.25,
                            py: 0.5,
                            borderRadius: "12px",
                            border: "1px solid",
                            borderColor: matchTotal > 0 ? "#bae6fd" : "#fecaca",
                            bgcolor: matchTotal > 0 ? "rgba(255,255,255,0.95)" : "#fef2f2",
                            minWidth: isMobile ? 72 : 88,
                            justifyContent: "center",
                        }}
                    >
                        <Stack spacing={0} alignItems="center">
                            <Typography
                                sx={{
                                    fontSize: isMobile ? 11 : 12,
                                    fontWeight: 700,
                                    color: matchTotal > 0 ? "#0369a1" : "#b91c1c",
                                    fontFamily: "manrope-med, sans-serif",
                                    whiteSpace: "nowrap",
                                    lineHeight: 1.2,
                                }}
                            >
                                {matchTotal > 0 ? `${matchPosition} / ${matchTotal}` : "0 / 0"}
                            </Typography>
                            {matchTotal > 0 && matchNav.columnLabel && (
                                <Typography
                                    sx={{
                                        fontSize: isMobile ? 9 : 10,
                                        fontWeight: 600,
                                        color: "#6b7280",
                                        fontFamily: "manrope-reg, sans-serif",
                                        whiteSpace: "nowrap",
                                        lineHeight: 1.2,
                                        maxWidth: isMobile ? 100 : 140,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                    }}
                                >
                                    {matchNav.columnLabel} (
                                    {(matchNav.columnMatchIndex ?? 0) + 1}/
                                    {matchNav.columnMatchTotal || 1})
                                </Typography>
                            )}
                        </Stack>
                    </Box>
                    <Tooltip title="Previous match" arrow>
                        <span>
                            <IconButton
                                size="small"
                                onClick={() => matchNav.goPrev?.()}
                                disabled={!matchNav.goPrev || matchTotal < 2}
                                aria-label="Previous match"
                                sx={{ p: 0.35 }}
                            >
                                <KeyboardArrowUpIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title="Next match" arrow>
                        <span>
                            <IconButton
                                size="small"
                                onClick={() => matchNav.goNext?.()}
                                disabled={!matchNav.goNext || matchTotal < 2}
                                aria-label="Next match"
                                sx={{ p: 0.35 }}
                            >
                                <KeyboardArrowDownIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>
            )}

            {filterType.type === "number" && (
                <Stack direction="column" alignItems="center" sx={{ mt: isMobile ? 0 : 0.25 }}>
                    <Tooltip title="and greater" placement="top" arrow>
                        <IconButton
                            onClick={() =>
                                setFilterType((prev) => ({
                                    ...prev,
                                    operation: prev.operation === "greaterThan" ? "" : "greaterThan",
                                }))
                            }
                            aria-label="Greater than"
                            size="small"
                            disableRipple
                            sx={{
                                width: "20px",
                                height: "20px",
                                mb: 0.2,
                                border: "2px solid grey",
                                borderRadius: "50%",
                                ...(isGreaterThan && { borderColor: "#008000" }),
                            }}
                        >
                            <SvgIcon alt="Greater than" sx={{ width: "14px", height: "14px" }}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24">
                                    <path
                                        fill="currentColor"
                                        fillRule="evenodd"
                                        d="m7.25 15l7.5-5l-7.5-5a.901.901 0 1 1 1-1.5l8.502 5.668a1 1 0 0 1 0 1.664L8.25 16.5a.901.901 0 1 1-1-1.5"
                                    />
                                    <path fill="currentColor" d="M17 20.998H7a1 1 0 0 1 0-2h10a1 1 0 0 1 0 2" />
                                </svg>
                            </SvgIcon>
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="and lower" placement="bottom" arrow>
                        <IconButton
                            onClick={() =>
                                setFilterType((prev) => ({
                                    ...prev,
                                    operation: prev.operation === "lessThan" ? "" : "lessThan",
                                }))
                            }
                            aria-label="Less than"
                            size="small"
                            disableRipple
                            sx={{
                                width: "20px",
                                height: "20px",
                                mt: 0.2,
                                border: "2px solid grey",
                                borderRadius: "50%",
                                ...(isLessThan && { borderColor: "#008000" }),
                            }}
                        >
                            <SvgIcon alt="Less than" sx={{ width: "14px", height: "14px" }}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24">
                                    <path
                                        fill="currentColor"
                                        fillRule="evenodd"
                                        d="m16.75 15l-7.5-5l7.5-5a.901.901 0 1 0-1-1.5L7.248 9.168a1 1 0 0 0 0 1.664L15.75 16.5a.901.901 0 1 0 1-1.5"
                                    />
                                    <path fill="currentColor" d="M17 20.998H7a1 1 0 0 1 0-2h10a1 1 0 0 1 0 2" />
                                </svg>
                            </SvgIcon>
                        </IconButton>
                    </Tooltip>
                </Stack>
            )}

            {showColumnPicker && (
                <Menu
                    anchorEl={columnMenuAnchorEl}
                    open={Boolean(columnMenuAnchorEl)}
                    onClose={handleColumnMenuClose}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                    PaperProps={{
                        sx: {
                            mt: 0.5,
                            borderRadius: 2,
                            border: "1px solid #e5e7eb",
                            boxShadow: "0 10px 28px rgba(15, 23, 42, 0.12)",
                            maxHeight: 380,
                            minWidth: isMobile ? 260 : 300,
                        },
                    }}
                    MenuListProps={{ dense: true, disableAutoFocusItem: true }}
                >
                    <MenuItem disabled dense sx={{ opacity: 1, py: 1, bgcolor: "#f8fafc" }}>
                        <ListItemText
                            primary="Columns to search"
                            secondary="Highlights only in selected columns"
                            primaryTypographyProps={{ fontSize: "0.75rem", fontWeight: 600 }}
                            secondaryTypographyProps={{ fontSize: "0.7rem", color: "#6b7280" }}
                        />
                    </MenuItem>
                    <Divider sx={{ mb: 0.5 }} />

                    {showColumnMenuSearch && (
                        <>
                            <Box sx={{ px: 1.5, py: 1 }} onClick={(e) => e.stopPropagation()}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    placeholder="Filter columns..."
                                    value={columnFilterQuery}
                                    onChange={(e) => setColumnFilterQuery(e.target.value)}
                                    onClick={(e) => e.stopPropagation()}
                                    onKeyDown={(e) => e.stopPropagation()}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon sx={{ color: "#9ca3af", fontSize: 18 }} />
                                            </InputAdornment>
                                        ),
                                        sx: { borderRadius: 1.5, bgcolor: "#f9fafb", height: 36 },
                                    }}
                                />
                            </Box>
                            <Divider sx={{ mb: 0.5 }} />
                        </>
                    )}

                    <MenuItem key="selectAll" dense onClick={(e) => e.preventDefault()}>
                        <Checkbox
                            size="small"
                            onChange={handleChangeSelectAll}
                            checked={isAllColumnsSelected}
                            indeterminate={isSomeColumnsSelected}
                            onClick={(e) => e.stopPropagation()}
                        />
                        <ListItemText
                            primary="Select all"
                            primaryTypographyProps={{ fontWeight: 600, fontSize: "0.8125rem" }}
                        />
                    </MenuItem>
                    <Divider sx={{ my: 0.5 }} />

                    {filteredQuestions?.length > 0 ? (
                        filteredQuestions.map((column) => {
                            const accessor = column?.question?.accessor;
                            const isChecked = selectedColumns.indexOf(accessor) > -1;
                            return (
                                <MenuItem
                                    key={column?.question?._id || accessor}
                                    dense
                                    onClick={() => handleToggleColumn(accessor)}
                                >
                                    <Checkbox size="small" checked={isChecked} tabIndex={-1} disableRipple />
                                    <ListItemText primary={getColumnDisplayLabel(column)} />
                                </MenuItem>
                            );
                        })
                    ) : (
                        <MenuItem disabled dense>
                            <ListItemText
                                primary="No columns match your filter"
                                primaryTypographyProps={{ fontSize: "0.8125rem", color: "#6b7280" }}
                            />
                        </MenuItem>
                    )}
                </Menu>
            )}
        </Stack>
    );
}
